import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { shouldClearPortalStateForAuthSessionChange } from '@/lib/portalAuthSession';
import {
  resolveCurrentPortalIdentity,
  type PortalIdentityResolverResult,
} from '@/lib/portalIdentityResolver';

type PortalIdentityResolverClient = Parameters<typeof resolveCurrentPortalIdentity>[0];

type UserRole = 'org_owner' | 'org_admin' | 'finance' | 'recruiter' | 'hiring_manager' | 'viewer' | 'talent_coordinator' | 'finance_offer_approver' | 'interview_reviewer' | null;

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: { full_name: string; email: string; org_id: string | null; user_type: string; status: string } | null;
  role: UserRole;
  loading: boolean;
  portalIdentityDiagnostic: PortalIdentityResolverResult | null;
  portalIdentityLoading: boolean;
  refreshProfile: () => Promise<void>;
  refreshPortalIdentityDiagnostic: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  role: null,
  loading: true,
  portalIdentityDiagnostic: null,
  portalIdentityLoading: false,
  refreshProfile: async () => {},
  refreshPortalIdentityDiagnostic: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<AuthContextType['profile']>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);
  const [portalIdentityDiagnostic, setPortalIdentityDiagnostic] =
    useState<PortalIdentityResolverResult | null>(null);
  const [portalIdentityLoading, setPortalIdentityLoading] = useState(false);
  const portalIdentityRequestSeqRef = useRef(0);
  const portalIdentityInFlightUserRef = useRef<string | null>(null);
  const activeHireUserIdRef = useRef<string | null>(null);

  const clearPortalIdentityDiagnostic = useCallback(() => {
    portalIdentityRequestSeqRef.current += 1;
    portalIdentityInFlightUserRef.current = null;
    setPortalIdentityDiagnostic(null);
    setPortalIdentityLoading(false);
  }, []);

  const runPortalIdentityDiagnostic = useCallback(
    async (userId: string, options: { force?: boolean } = {}) => {
      if (
        !options.force
        && portalIdentityInFlightUserRef.current === userId
      ) {
        return;
      }

      const requestSeq = portalIdentityRequestSeqRef.current + 1;
      portalIdentityRequestSeqRef.current = requestSeq;
      portalIdentityInFlightUserRef.current = userId;
      setPortalIdentityLoading(true);

      try {
        const result = await resolveCurrentPortalIdentity(
          supabase as unknown as PortalIdentityResolverClient,
          'hire',
        );

        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          setPortalIdentityDiagnostic(result);
        }
      } catch {
        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          setPortalIdentityDiagnostic({
            ok: false,
            ready: false,
            reason: 'resolver_error',
            portal_auth_identity_id: null,
            portal: null,
            user_type: null,
            auth_provider: null,
            auth_instance_id: null,
            auth_user_id: null,
            status: null,
            auth_identity_kind: null,
          });
        }
      } finally {
        if (
          portalIdentityRequestSeqRef.current === requestSeq
          && portalIdentityInFlightUserRef.current === userId
        ) {
          portalIdentityInFlightUserRef.current = null;
          setPortalIdentityLoading(false);
        }
      }
    },
    [],
  );

  const fetchProfileAndRole = useCallback(async (userId: string) => {
    const [profileRes, roleRes] = await Promise.all([
      supabase
        .from('hire_profiles')
        .select('full_name, email, org_id, status')
        .eq('user_id', userId)
        .single(),
      supabase
        .from('hire_user_roles')
        .select('role')
        .eq('user_id', userId),
    ]);

    if (activeHireUserIdRef.current !== userId) {
      return;
    }

    const nextProfile = !profileRes.error && profileRes.data
      ? { ...profileRes.data, user_type: 'employer' }
      : null;

    if (!profileRes.error && profileRes.data) {
      setProfile(nextProfile);
    }

    if (!roleRes.error && roleRes.data) {
      // A user can hold multiple org roles (e.g. org_owner + finance).
      // Pick the highest-privilege role for client-side UX gating.
      // Server-side RLS / SECURITY DEFINER fns continue to evaluate every
      // role independently — this is a UI-only resolution.
      const ROLE_PRIORITY: Record<string, number> = {
        org_owner: 100,
        org_admin: 90,
        finance: 80,
        finance_offer_approver: 75,
        hiring_manager: 70,
        recruiter: 60,
        talent_coordinator: 55,
        interview_reviewer: 50,
        viewer: 10,
      };
      const rows = (roleRes.data ?? []) as Array<{ role: string }>;
      const best = rows
        .map(r => r.role)
        .sort((a, b) => (ROLE_PRIORITY[b] ?? 0) - (ROLE_PRIORITY[a] ?? 0))[0];
      setRole((best as UserRole) ?? null);
    } else {
      setRole(null);
    }

    if (nextProfile?.user_type === 'employer') {
      void runPortalIdentityDiagnostic(userId);
    } else {
      clearPortalIdentityDiagnostic();
    }
  }, [clearPortalIdentityDiagnostic, runPortalIdentityDiagnostic]);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    await fetchProfileAndRole(user.id);
  }, [fetchProfileAndRole, user?.id]);

  const refreshPortalIdentityDiagnostic = useCallback(async () => {
    if (!user?.id || profile?.user_type !== 'employer') {
      clearPortalIdentityDiagnostic();
      return;
    }

    await runPortalIdentityDiagnostic(user.id, { force: true });
  }, [clearPortalIdentityDiagnostic, profile?.user_type, runPortalIdentityDiagnostic, user?.id]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const previousUserId = activeHireUserIdRef.current;
        const nextUserId = session?.user?.id ?? null;
        const shouldClearProfile = shouldClearPortalStateForAuthSessionChange(previousUserId, nextUserId);
        activeHireUserIdRef.current = nextUserId;
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          if (shouldClearProfile) {
            setProfile(null);
            setRole(null);
            clearPortalIdentityDiagnostic();
          }
          // Use setTimeout to avoid Supabase deadlock
          setTimeout(() => fetchProfileAndRole(session.user.id), 0);
        } else {
          setProfile(null);
          setRole(null);
          clearPortalIdentityDiagnostic();
        }
        setLoading(false);
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      const previousUserId = activeHireUserIdRef.current;
      const nextUserId = session?.user?.id ?? null;
      const shouldClearProfile = shouldClearPortalStateForAuthSessionChange(previousUserId, nextUserId);
      activeHireUserIdRef.current = nextUserId;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        if (shouldClearProfile) {
          setProfile(null);
          setRole(null);
          clearPortalIdentityDiagnostic();
        }
        fetchProfileAndRole(session.user.id);
      } else {
        clearPortalIdentityDiagnostic();
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [clearPortalIdentityDiagnostic, fetchProfileAndRole]);

  const signOut = async () => {
    await supabase.auth.signOut();
    activeHireUserIdRef.current = null;
    setSession(null);
    setUser(null);
    setProfile(null);
    setRole(null);
    clearPortalIdentityDiagnostic();
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        role,
        loading,
        portalIdentityDiagnostic,
        portalIdentityLoading,
        refreshProfile,
        refreshPortalIdentityDiagnostic,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
